import { useAppDispatch, useAppSelector } from 'src/hooks';
import React, { useEffect, useRef, useState } from 'react';
import { functionLibrary } from 'src/lib';
import './table-columns.scss';
import { REGEX_FOR_DECIMAL, REGEX_FOR_WHOLE_NUMBER } from 'src/constants';
import { Header, TableComponent } from '../../types/generic-table.model';
import { execRegexInstance, getRegexInstance } from '../../utils/number-utils';
import { handleNumericInputValueChange } from '../../../common/features/table/store/table-slice';
import { handleNumericInputValueChange as handleNumericInputValueChange2 } from '../../../common/features/table/store/table-slice2';

export default function AbsoluteInputBox({
  row,
  columnMetadata,
  uniqueIDColumns,
  componentType,
  comparisonColumnMetadata = { parent: { parent_field: '' }, chideren: [] },
  inputsDisabled
}: {
  row: any;
  columnMetadata: Header;
  uniqueIDColumns: Header[];
  componentType: TableComponent;
  comparisonColumnMetadata: {
    parent: { parent_field: string };
    chideren: any[];
  };
  inputsDisabled: boolean;
}) {
  const appDispatch = useAppDispatch();
  const { kpiSelection, toggle2 } = useAppSelector(
    (state) => state.pricingSimulator
  );

  const getPrefix = () => {
    if (
      columnMetadata?.parent?.prefix_label &&
      (row.original[columnMetadata.parent.prefix_label] !== null ||
        row.original[columnMetadata.parent.prefix_label] !== undefined)
    ) {
      return columnMetadata.parent.prefix_label;
    }
    return '';
  };

  const getSuffix = () => columnMetadata?.parent?.suffix_label;

  const getFormattedValue = (selectedValue: number) => {
    const regexForWholeNumPattern = getRegexInstance(REGEX_FOR_WHOLE_NUMBER);
    const wholeNumRegexData = execRegexInstance(
      regexForWholeNumPattern,
      columnMetadata?.parent?.number_format
    );
    const regexForDecimal = getRegexInstance(REGEX_FOR_DECIMAL);
    const decimalRegexData = execRegexInstance(
      regexForDecimal,
      columnMetadata?.parent?.number_format
    );
    if (decimalRegexData) {
      return functionLibrary.roundUp(
        selectedValue,
        decimalRegexData[decimalRegexData.length - 1].length
      );
    }
    if (wholeNumRegexData) {
      return Math.round(selectedValue);
    }
    return selectedValue;
  };

  const [value, setValue] = useState(
    getFormattedValue(row.original[columnMetadata?.parent?.parent_field])
  );
  const previousValueRef = useRef(value); // Store the previous value in a ref

  useEffect(() => {
    // Update the value when the row's data changes (external change)
    const formattedValue = getFormattedValue(
      row.original[columnMetadata?.parent?.parent_field] as number
    );
    if (previousValueRef.current !== formattedValue) {
      setValue(formattedValue);
    }
    previousValueRef.current = formattedValue; // Update the previous value after change
  }, [row.original[columnMetadata?.parent?.parent_field]]); // Only trigger when this specific value changes

  const handleValueChange: React.FocusEventHandler<HTMLInputElement> = (e) => {
    setValue(Number(e.target.value));
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    const formattedNewValue = getFormattedValue(Number(e.target.value));
    setValue(formattedNewValue);
    if (
      previousValueRef.current ===
        row.original[columnMetadata?.parent?.parent_field] &&
      previousValueRef.current === formattedNewValue
    ) {
      return;
    }

    const rowsData = row.getParentRows().map((data) => data.original);
    if (TableComponent.COGS === componentType) {
      appDispatch(
        handleNumericInputValueChange2({
          columnMetadata,
          newValue: +e.target.value,
          dataRow: row.original,
          changedRows: rowsData,
          uniqueIDColumns,
          depth: row.depth
        })
      );
    } else {
      appDispatch(
        handleNumericInputValueChange({
          columnMetadata,
          newValue: +e.target.value,
          dataRow: row.original,
          changedRows: rowsData,
          uniqueIDColumns,
          depth: row.depth
        })
      );
    }
  };

  if (
    comparisonColumnMetadata &&
    row.original[comparisonColumnMetadata.parent.parent_field] === value
  ) {
    if (
      kpiSelection === toggle2.name &&
      row.getCanExpand() &&
      componentType !== TableComponent.RENOVATION
    ) {
      return (
        <span>
          {getPrefix()}
          {Number.isNaN(value) ? '-' : value}
          {getSuffix()}
        </span>
      );
    }
    return (
      <div className='input-box-container'>
        <span className='prefix-cell'>{getPrefix()}</span>
        <input
          value={value}
          onChange={handleValueChange}
          onBlur={handleBlur}
          className='table-row-input-box'
          type='number'
          style={{
            paddingRight: columnMetadata?.parent?.suffix_label ? '10px' : '0px'
          }}
          disabled={(componentType=== TableComponent.RENOVATION || componentType === TableComponent.COGS) && inputsDisabled === false ? false :
            row.getCanExpand() || inputsDisabled}
          placeholder='-'
        />
        <span className='suffix-cell'>{getSuffix()}</span>
      </div>
    );
  }

  if (
    kpiSelection === toggle2.name &&
    row.getCanExpand() &&
    componentType !== TableComponent.RENOVATION
  ) {
    return (
      <span>
        {getPrefix()}
        {Number.isNaN(value) ? '-' : value}
        {getSuffix()}
      </span>
    );
  }

  return (
    <div className='input-box-container'>
      <span className='prefix-cell'>{getPrefix()}</span>
      <input
        value={value} // Convert to string to remove leading zeros
        onChange={handleValueChange}
        onBlur={handleBlur}
        disabled={ (componentType=== TableComponent.RENOVATION || componentType === TableComponent.COGS) && inputsDisabled === false ? false :
          row.getCanExpand() || inputsDisabled}
        className='table-row-input-box'
        style={{
          paddingRight: columnMetadata?.parent?.suffix_label ? '10px' : '0px'
        }}
        type='number'
        placeholder='-'
      />
      <span className='suffix-cell'>{getSuffix()}</span>
    </div>
  );
}
